import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Shield, Mail, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { authService } from '../../services/authService';
import { useLanguage } from '../../hooks/useLanguage';

type ResponseType = 'none' | 'password_reset' | 'email_verification' | 'error';

export const ForgotPasswordForm: React.FC = () => {
    const { t } = useTranslation();
    const { isRTL } = useLanguage();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [responseType, setResponseType] = useState<ResponseType>('none');
    const emailInputRef = useRef<HTMLInputElement>(null);

    // Configuration de la validation personnalisée
    const setupCustomValidation = () => {
        const input = emailInputRef.current;
        if (!input) return;

        const handleInvalid = () => {
            if (input.validity.valueMissing) {
                input.setCustomValidity(t('forgotPassword.validation.emailRequired'));
            } else if (input.validity.typeMismatch) {
                input.setCustomValidity(t('forgotPassword.validation.emailInvalid'));
            }
        };

        const handleInput = () => {
            input.setCustomValidity('');
        };

        input.addEventListener('invalid', handleInvalid);
        input.addEventListener('input', handleInput);

        return () => {
            input.removeEventListener('invalid', handleInvalid);
            input.removeEventListener('input', handleInput);
        };
    };

    useEffect(() => {
        const cleanup = setupCustomValidation();
        return cleanup;
    }, [t]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setResponseType('none');

        if (!email) {
            setError(t('forgotPassword.validation.emailRequired'));
            return;
        }

        try {
            setLoading(true);
            const response = await authService.requestPasswordReset(email);

            if (response.emailNotVerified && response.verificationEmailSent) {
                setResponseType('email_verification');
            } else if (response.passwordResetSent) {
                setResponseType('password_reset');
            } else {
                setResponseType('password_reset');
            }

        } catch (err: any) {
            setError( t('forgotPassword.validation.sendError'));
            setResponseType('error');
        } finally {
            setLoading(false);
        }
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        e.target.setCustomValidity('');
    };

    const renderSuccessMessage = () => {
        switch (responseType) {
            case 'email_verification':
                return (
                    <div className="rounded-lg bg-blue-50 p-4 text-sm border border-blue-200">
                        <div className="flex items-start">
                            <Info className={`h-5 w-5 text-blue-400 ${isRTL ? 'ml-2' : 'mr-2'} mt-0.5`} />
                            <div>
                                <h3 className="text-blue-800 font-medium mb-2">
                                    {t('forgotPassword.verification.title')}
                                </h3>
                                <p className="text-blue-700 mb-3">
                                    {t('forgotPassword.verification.accountNotVerified')}
                                </p>
                                <p className="text-blue-700">
                                    {t('forgotPassword.verification.verificationSent', { email })}
                                </p>
                                <p className="text-blue-600 text-xs mt-2">
                                    {t('forgotPassword.verification.afterActivation')}
                                </p>
                            </div>
                        </div>
                    </div>
                );

            case 'password_reset':
                return (
                    <div className="rounded-lg bg-green-50 p-4 text-sm border border-green-200">
                        <div className="flex items-center">
                            <CheckCircle className={`h-5 w-5 text-green-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span className="text-green-700">
                                {t('forgotPassword.success.resetSent')}
                            </span>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    const renderActionButtons = () => {
        switch (responseType) {
            case 'email_verification':
                return (
                    <div className="flex flex-col space-y-3">
                        <Link
                            to="/login"
                            className="flex justify-center items-center py-2 px-4 border border-blue-300 rounded-lg text-sm font-medium text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-150"
                        >
                            {t('forgotPassword.actions.backToLogin')}
                        </Link>
                        <p className="text-xs text-gray-500 text-center">
                            {t('forgotPassword.verification.afterVerificationNote')}
                        </p>
                    </div>
                );

            case 'password_reset':
                return (
                    <div className="flex flex-col space-y-3">
                        <Link
                            to="/reset-password"
                            className="flex justify-center items-center py-2 px-4 border border-blue-300 rounded-lg text-sm font-medium text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-150"
                        >
                            {t('forgotPassword.actions.continueToReset')}
                        </Link>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className={`min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white py-12 px-4 sm:px-6 lg:px-8 ${isRTL ? 'rtl' : ''}`}>
            <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-xl">
                <div className="flex flex-col items-center">
                    <Shield className="h-12 w-12 text-blue-600" />
                    <h2 className="mt-4 text-center text-3xl font-extrabold text-gray-900">
                        {t('forgotPassword.title')}
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        {t('forgotPassword.subtitle')}
                    </p>
                </div>

                {responseType === 'none' || responseType === 'error' ? (
                    <form className="mt-8 space-y-6" onSubmit={handleSubmit} noValidate>
                        {error && (
                            <div className="rounded-lg bg-red-50 p-4 text-sm flex items-center border border-red-200">
                                <AlertTriangle className={`h-5 w-5 text-red-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                                <span className="text-red-700">{error}</span>
                            </div>
                        )}

                        <div className="space-y-4">
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('forgotPassword.form.email')}
                                </label>
                                <div className="relative">
                                    <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                                        <Mail className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        ref={emailInputRef}
                                        id="email"
                                        name="email"
                                        type="email"
                                        required
                                        className={`block w-full ${isRTL ? 'pr-10 pl-3' : 'pl-10 pr-3'} py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                                        placeholder={t('forgotPassword.form.emailPlaceholder')}
                                        value={email}
                                        onChange={handleEmailChange}
                                        dir="ltr"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col space-y-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className={`flex justify-center items-center py-2 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-150 ${
                                    loading ? 'opacity-75 cursor-not-allowed' : ''
                                }`}
                            >
                                {loading ? (
                                    <>
                                        <svg className={`animate-spin ${isRTL ? 'mr-1 ml-3' : '-ml-1 mr-3'} h-5 w-5 text-white`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        {t('forgotPassword.form.sending')}
                                    </>
                                ) : (
                                    t('forgotPassword.form.submit')
                                )}
                            </button>

                            <div className="text-center">
                                <Link
                                    to="/login"
                                    className="text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors duration-150"
                                >
                                    {t('forgotPassword.actions.backToLogin')}
                                </Link>
                            </div>
                        </div>
                    </form>
                ) : (
                    <div className="mt-8 space-y-6">
                        {renderSuccessMessage()}
                        {renderActionButtons()}

                        <div className="text-center pt-4">
                            <button
                                onClick={() => {
                                    setResponseType('none');
                                    setEmail('');
                                    setError('');
                                }}
                                className="text-sm font-medium text-gray-600 hover:text-gray-500 transition-colors duration-150"
                            >
                                {t('forgotPassword.actions.tryAnotherEmail')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};